// client/src/pages/sections/groups/PhaseSignoffBar.js
// End-of-phase attestation row rendered at the bottom of Phase 2 (SG) and
// Phase 3 (RG) in the Client Groups DQ panel. Three visual states:
//
//   1. Not signed off, can't yet: button disabled with a tooltip ("Clear
//      all open issues in this phase first.")
//   2. Not signed off, ready:     primary button "Sign off Phase X".
//   3. Signed off:                green confirmation pill + a small Undo.
//
// Posts to /api/dgPhaseSignoff/{period} with { phase, undo? }. On success
// calls onChanged() so the panel can refetch and pick up the new state.

import { useEffect, useRef, useState } from 'react';
import { getDemo } from '../../../lib/tourDemo';
import { emitToast, errorText } from './toast';
import { useNameFor } from './useNameFor';

// Sign-off / undo POSTs go out one at a time, in click order, across every bar in the tab.
let signoffChain = Promise.resolve();
function queueSignoff(run) {
  const mine = signoffChain.then(run, run);
  signoffChain = mine.catch(() => {});
  return mine;
}

const PHASE_LABEL = {
  sg:         { name: 'Signer Groups',          short: 'Step 3' },
  rg:         { name: 'Relationship Groups',    short: 'Step 4' },
  standalone: { name: 'Standalone Individuals', short: 'Step 5' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}

export default function PhaseSignoffBar({
  phase,                // 'sg' | 'rg' | 'standalone'
  period,               // active period (e.g. '2026-05')
  signoff,              // null | { signedOffAt, signedOffBy }
  canSignOff,           // boolean — false when there are still open issues
  onChanged,            // () => void   — refetch after sign or undo
  testMode,             // boolean — when true, signoff is captured in the test undo log
  testSessionId,        // string — required to capture the signoff in test mode
  isAdmin,              // boolean — only admins may undo a sign-off
  blockedNote,          // optional text for the disabled state (replaces the generic "resolve open issues" one)
}) {
  const nameFor = useNameFor();
  const [err, setErr]   = useState(null);
  const inFlight = useRef(0);   // sign-off / undo POSTs queued or in flight from this bar
  const meta = PHASE_LABEL[phase] || { name: phase, short: phase };

  // Optimistic sign-off: reflect the new state instantly so the button flips
  // to the signed-off pill the moment it's clicked, instead of staying
  // clickable while the POST + parent refetch settle. `override` is the local
  // intent (object = signed off; null = undone); undefined = defer to the
  // server-truth `signoff` prop. We drop the override whenever the prop
  // changes (the refetch landed) and revert it on a failed POST.
  const [override, setOverride] = useState(undefined);
  useEffect(() => { setOverride(undefined); }, [signoff]);
  const effectiveSignoff = override !== undefined ? override : signoff;

  async function doSignoff(undo) {
    // Spotlight tour demo mode: reflect the flip locally so the button shows
    // its signed-off (or reopened) state, but never POST to the real endpoint.
    if (getDemo()) {
      setErr(null);
      setOverride(undo ? null : { optimistic: true });
      return;
    }
    setErr(null);
    // Optimistic: show signed-off (or unsigned) right away; revert on failure.
    // Clicks are never ignored while a save is in flight — they queue behind it
    // (postJsonQueued keeps them in order) — so there is no disabled/"Working…" state.
    setOverride(undo ? null : { optimistic: true });
    inFlight.current += 1;
    const verb = undo ? 'undo the sign-off' : 'sign off';
    try {
      const res = await queueSignoff(() => fetch(`/api/dgPhaseSignoff/${encodeURIComponent(period)}`, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({
          phase,
          undo:      Boolean(undo),
          // In test mode the signoff is captured in test_undo_log so
          // Undo-all can reverse it (see dgPhaseSignoff).
          testMode:  Boolean(testMode),
          sessionId: testMode ? testSessionId : null,
        }),
      }));
      const text = await res.text();
      if (!res.ok) {
        // The server answers a not-ready step (409) and other refusals as JSON { error }; show that text.
        let message = text;
        try { message = JSON.parse(text).error || text; } catch { /* plain-text body */ }
        throw new Error(errorText({ message: message || `HTTP ${res.status}` }));
      }
      inFlight.current -= 1;
      // Reconcile quietly: the refetch lands the server's stamp (who / when) and drops the override.
      // Skipped while a later click is still queued, so its optimistic state isn't overwritten.
      if (inFlight.current === 0) onChanged && onChanged({ phase, undone: Boolean(undo) });
    } catch (e) {
      inFlight.current -= 1;
      setOverride(undefined);   // revert the optimistic flip to the server's value
      setErr(e.message);
      emitToast({ kind: 'error', message: `Couldn't ${verb}: ${e.message}. Restored.` });
      // A later click may have been queued behind this one; resync from the server.
      if (inFlight.current > 0) onChanged && onChanged({ phase, undone: false });
    }
  }

  // Nothing in this step is awaiting review (every client was signed off in an earlier cycle):
  // no sign-off is needed, so there's nothing to click or undo.
  if (effectiveSignoff && effectiveSignoff.auto) {
    return (
      <div className="cg-signoff-bar cg-signoff-bar--done">
        <i className="fas fa-check-circle cg-signoff-icon" aria-hidden="true" />
        <span className="cg-signoff-text">
          <strong>{meta.short} ({meta.name})</strong> — nothing to sign off: every client here
          was reviewed in an earlier cycle.
        </span>
      </div>
    );
  }

  if (effectiveSignoff) {
    return (
      <div className="cg-signoff-bar cg-signoff-bar--done">
        <i className="fas fa-check-circle cg-signoff-icon" aria-hidden="true" />
        <span className="cg-signoff-text">
          <strong>{meta.short} ({meta.name})</strong> signed off
          {effectiveSignoff.signedOffBy ? <> by <strong>{nameFor(effectiveSignoff.signedOffBy)}</strong></> : null}
          {effectiveSignoff.signedOffAt ? <> on {fmtDate(effectiveSignoff.signedOffAt)}</> : null}.
        </span>
        {/* Signing off no longer freezes the step; the sign-off stamps the partner's
            unreviewed clients. Only an admin may undo it (dgPhaseSignoff enforces this). */}
        {!isAdmin ? (
          <span className="cg-signoff-text" style={{ opacity: 0.7 }}>
            · An admin can undo this sign-off if needed.
          </span>
        ) : (
          <button
            type="button"
            className="cg-signoff-undo"
            onClick={() => doSignoff(true)}
            title="Walk this attestation back"
          >
            Undo
          </button>
        )}
        {err && <span className="cg-signoff-error">· {err}</span>}
      </div>
    );
  }

  if (!canSignOff) {
    return (
      <div className="cg-signoff-bar cg-signoff-bar--pending">
        <span className="cg-signoff-text">
          <strong>{meta.short} ({meta.name})</strong> — {blockedNote || 'sign-off available once every open issue in this phase is resolved.'}
        </span>
        <button type="button" className="cg-signoff-btn" disabled title={blockedNote || 'Resolve all open issues in this phase first.'}>
          Sign off
        </button>
      </div>
    );
  }

  return (
    <div className="cg-signoff-bar cg-signoff-bar--ready">
      <span className="cg-signoff-text">
        <strong>{meta.short} ({meta.name})</strong> is ready for your attestation.
      </span>
      <button
        type="button"
        className="cg-signoff-btn cg-signoff-btn--primary"
        onClick={() => doSignoff(false)}
      >
        {`Sign off ${meta.short}`}
      </button>
      {err && <span className="cg-signoff-error">· {err}</span>}
    </div>
  );
}
